import React, { useMemo, useEffect } from 'react';
import { Row, Col, Form, Input, Select, Switch, Tooltip } from 'antd';
import { MoonOutlined, SunOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';

const { Option } = Select;

// Helper that wraps any label with an info tooltip.
const labelWithInfo = (label, tooltip) => (
    <span>
        {label}
        <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
        </Tooltip>
    </span>
);

// feature -> option metadata for the mode select
const DEVICE_MODE_META = {
    entranceControl: { label: 'Entrance Control', feature: 'deviceConfig.mode.entranceControl' },
    cloakroom: { label: 'Cloakroom', feature: 'deviceConfig.mode.cloakroom' },
    nfcLink: { label: 'NFC Link', feature: 'deviceConfig.mode.nfcLink' },
};

const normalizeType = (value) => (value ?? '').toString().trim().toLowerCase();

const TabEvent = ({
                      form,
                      selectedEventId,
                      setSelectedEventId,
                      handleEventChange,
                      events,
                      programmePoints,
                      locations,
                      isProgrammePointDisabled,
                      isLocationDisabled,
                      themeSwitchChecked
                  }) => {
    const { hasFeature, isAdmin } = useAuth?.() || { hasFeature: () => false, isAdmin: false };

    // Read both possible field names to avoid mismatch
    const configTypeWatch = Form.useWatch('ConfigType', form);
    const typeWatch = Form.useWatch('Type', form);
    const currentMode = Form.useWatch('Mode', form);

    const normalizedConfigType = normalizeType(configTypeWatch || typeWatch);

    const allowedModes = useMemo(() => {
        // IMPORTANT: only mobile gets selectable modes
        if (normalizedConfigType !== 'mobile') return [];

        const modes = [];
        if (hasFeature(DEVICE_MODE_META.entranceControl.feature)) modes.push('entranceControl');
        if (hasFeature(DEVICE_MODE_META.cloakroom.feature)) modes.push('cloakroom');
        if (hasFeature(DEVICE_MODE_META.nfcLink.feature)) modes.push('nfcLink');

        if (!modes.length && isAdmin) return ['entranceControl', 'cloakroom', 'nfcLink'];

        return modes.length ? modes : ['entranceControl'];
    }, [normalizedConfigType, hasFeature, isAdmin]);

    const showVCardKey = useMemo(() => {
        return hasFeature('deviceConfig.vCard') || isAdmin;
    }, [hasFeature, isAdmin]);

    const hideModeSelector =
        normalizedConfigType === 'desktop' || normalizedConfigType === 'leadcapture';

    useEffect(() => {
        const mode = (currentMode ?? '').toString().trim();

        if (normalizedConfigType === 'desktop') {
            if (mode !== 'selfCheckIn') {
                form.setFieldsValue({ Mode: 'selfCheckIn' });
            }
            return;
        }

        if (normalizedConfigType === 'leadcapture') {
            if (mode !== 'leadcapture') {
                form.setFieldsValue({ Mode: 'leadcapture' });
            }
            return;
        }

        if (normalizedConfigType === 'mobile') {
            // keep DB value if valid
            if (mode && allowedModes.includes(mode)) return;

            form.setFieldsValue({
                Mode: allowedModes.length === 1 ? allowedModes[0] : null,
            });
        }
    }, [normalizedConfigType, currentMode, allowedModes, form]);

    const onEventChange = (eventId) => {
        setSelectedEventId(eventId);
        handleEventChange(eventId);
    };

    return (
        <>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        name="DeviceConfigName"
                        label="Device Config Name"
                        rules={[{ required: true, message: 'Please input the Device Config Name!' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>

                <Col
                    xs={12}
                    sm={12}
                    md={8}
                    style={{ display: hideModeSelector ? 'none' : 'block' }}
                >
                    <Form.Item shouldUpdate>
                        {() => {
                            const liveType = normalizeType(
                                form.getFieldValue('ConfigType') || form.getFieldValue('Type')
                            );

                            // never render the selector for desktop/leadcapture
                            if (liveType === 'desktop' || liveType === 'leadcapture') {
                                return null;
                            }

                            const mode = (form.getFieldValue('Mode') ?? '').toString().trim();
                            const hasMode = mode.length > 0;

                            const isAllowed = !hasMode || allowedModes.includes(mode);
                            const options = hasMode && !isAllowed ? [mode] : allowedModes;

                            return (
                                <Form.Item
                                    name="Mode"
                                    label={labelWithInfo('Mode', 'Select how this device configuration should operate')}
                                >
                                    <Select placeholder="Select mode" style={{ width: '80%' }}>
                                        {options.map((m) => (
                                            <Select.Option key={m} value={m} disabled={hasMode && !isAllowed}>
                                                {DEVICE_MODE_META[m]?.label ?? m}
                                                {hasMode && !isAllowed ? ' (not enabled for this client)' : ''}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Col>

                <Col span={4}>
                    <Form.Item
                        label="App Theme"
                        name="theme"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren={<MoonOutlined />}
                            unCheckedChildren={<SunOutlined />}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="SelectedEventId"
                        label="Selected Event"
                    >
                        <Select
                            placeholder="Select an event"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={onEventChange}
                            value={selectedEventId}
                            allowClear
                        >
                            <Option value={null} key="no-event">
                                No Event
                            </Option>
                            {events?.length > 0 ? (
                                events.map(event => (
                                    <Option key={event._id} value={event._id}>
                                        {event.mnemonic}
                                    </Option>
                                ))
                            ) : (
                                <Option value={null} key="no-events-available" disabled>
                                    No events available
                                </Option>
                            )}
                        </Select>
                    </Form.Item>
                </Col>

                {showVCardKey && (
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="VCardParticipantIdKey"
                            label={labelWithInfo(
                                'vCard Participant ID Key',
                                'Please introduce the vCard key/property name that contains the participant ID (e.g. X-LAMBDALOGIC-P-ID). Must start with X-.'
                            )}
                            rules={[
                                { max: 80, message: 'Key is too long.' },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();

                                        const trimmed = value.trim();

                                        if (!trimmed.toUpperCase().startsWith('X-')) {
                                            return Promise.reject(
                                                new Error('Custom vCard keys must start with "X-".')
                                            );
                                        }

                                        if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
                                            return Promise.reject(
                                                new Error('Only letters, numbers, "_" and "-" are allowed.')
                                            );
                                        }

                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder="X-LAMBDALOGIC-P-ID"
                                onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value) {
                                        form.setFieldsValue({
                                            VCardParticipantIdKey: value.trim().toUpperCase(),
                                        });
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                )}
            </Row>

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="SelectedProgrammePointId"
                        label="Programme Point"
                    >
                        <Select
                            placeholder="Select a programme point"
                            disabled={!selectedEventId || programmePoints?.length === 0 || isProgrammePointDisabled}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => {
                                const selectedPoint = programmePoints?.find(point => point.id === value);
                                form.setFieldsValue({
                                    SelectedProgrammePointId: value,
                                    SelectedProgrammePointName: selectedPoint ? selectedPoint.name[0].value : null,
                                    SelectedLocationId: null,
                                    SelectedLocationName: null,
                                });
                            }}
                        >
                            {programmePoints?.length > 0 ? (
                                <>
                                    <Option key="no-selection" value={null}>Not selected</Option>
                                    {programmePoints?.map(point => (
                                        <Option key={point.id} value={point.id}>
                                            {point.name[0].value}
                                        </Option>
                                    ))}
                                </>
                            ) : (
                                <Option value={null} key="no-points-available" disabled>
                                    No Programme Points Found
                                </Option>
                            )}
                        </Select>
                    </Form.Item>
                    <Form.Item name="SelectedProgrammePointName" hidden>
                        <Input />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                    <Form.Item
                        name="SelectedLocationId"
                        label="Location"
                    >
                        <Select
                            placeholder="Select a location"
                            disabled={!selectedEventId || locations?.length === 0 || isLocationDisabled}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => {
                                const selectedLocation = locations.find(location => location.id === value);
                                form.setFieldsValue({
                                    SelectedLocationId: value,
                                    SelectedLocationName: selectedLocation ? selectedLocation.name : null,
                                    SelectedProgrammePointId: null,
                                    SelectedProgrammePointName: null,
                                });
                            }}
                        >
                            {locations?.length > 0 ? (
                                <>
                                    <Option key="no-selection" value={null}>Not selected</Option>
                                    {locations.map(location => (
                                        <Option key={location.id} value={location.id}>
                                            {location.name}
                                        </Option>
                                    ))}
                                </>
                            ) : (
                                <Option value={null} key="no-location-available" disabled>
                                    No Locations Found
                                </Option>
                            )}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

export default TabEvent;